/**
 * Mid-session lockout. Once any call answers 403 `user_deactivated`, the whole
 * app gives way to one screen: no half-loaded page full of errors, and nothing
 * fetched as this person stays on screen. Signing out clears the session so
 * someone else can sign in on this browser.
 */

import { useEffect, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAccessStore } from "@/stores/access-store";
import { Logo } from "./logo";

export function AccessOff() {
  const [leaving, setLeaving] = useState(false);
  const signOut = () => {
    setLeaving(true);
    // The server may refuse even this for a turned-off account; the sign-in screen is the goal either way.
    void api
      .postExpectingRedirect("/auth/logout")
      .catch(() => undefined)
      .finally(() => window.location.assign("/login"));
  };
  return (
    <main className="grid min-h-dvh place-items-center bg-subtle px-6">
      <div className="box flex max-w-md flex-col items-center gap-3 px-7 py-7 text-center">
        <Logo size={32} />
        <h1 className="text-[17px] font-semibold">Your access has been turned off</h1>
        <p className="text-[13px] leading-relaxed text-muted">
          A workbench admin turned off your account, so you can't open projects here any more. Your work is
          kept. If you think this is a mistake, ask a workbench admin to turn it back on.
        </p>
        <button type="button" className="btn mt-1" onClick={signOut} disabled={leaving}>
          {leaving ? "Signing out…" : "Sign out"}
        </button>
      </div>
    </main>
  );
}

export function AccessGate({ children }: { children: ReactNode }) {
  const deactivated = useAccessStore((s) => s.deactivated);
  const queryClient = useQueryClient();
  useEffect(() => {
    // Stop polling and drop everything fetched as them.
    if (deactivated) queryClient.clear();
  }, [deactivated, queryClient]);
  return deactivated ? <AccessOff /> : <>{children}</>;
}
